import { oneDayInMilliseconds } from '../models/constants';
import { Cache, getHash } from './cache';
import { IDbCache } from '../models/cache';
import { ICacheClearArgs } from '../models/cache-clear-args';
import { ICacheReadArgs } from '../models/cache-read-args';
import { ICachedValue } from '../models/cached-value';
import { IDbCacheDAO } from '../models/db-cache-dao';
import { ISillyLogger } from '../models/silly-logger';

export class DbCache extends Cache implements IDbCache {
  protected cacheName = 'Db';
  private readonly cachedValueDAO: IDbCacheDAO;
  private readonly defaultRefreshAfterMilliseconds?: number;

  constructor(args: {
    cachedValueDAO: IDbCacheDAO;
    logger?: ISillyLogger;
    defaultRefreshAfterMilliseconds?: number;
  }) {
    super(args.logger);
    this.cachedValueDAO = args.cachedValueDAO;
    this.defaultRefreshAfterMilliseconds =
      args.defaultRefreshAfterMilliseconds || oneDayInMilliseconds;
  }

  public async read<T>(args: ICacheReadArgs<T>): Promise<T> {
    if (process.env.DISABLE_DB_CACHE === 'true') {
      return args.retrieve();
    }
    const hash = getHash(args.bucket, args.id);
    const cached = await this.cachedValueDAO.findByHash(hash);
    return this.handleCachedRead(
      cached,
      args,
      this.defaultRefreshAfterMilliseconds,
    );
  }

  public async clear(args?: ICacheClearArgs): Promise<void> {
    if (process.env.DISABLE_DB_CACHE === 'true') {
      return;
    }
    if (!args) {
      await this.cachedValueDAO.removeAll();
    } else {
      const hash = getHash(args.bucket, args.id);
      await this.cachedValueDAO.removeByHash(hash);
    }
  }

  public async readByBucket<T>(bucket: string): Promise<{ [id: string]: T }> {
    if (process.env.DISABLE_DB_CACHE === 'true') {
      return {};
    }
    const cachedValues = await this.cachedValueDAO.findByBucket(bucket);
    return cachedValues.reduce((acc, cached) => {
      const id = cached.hash.split(':')[1];
      acc[id] = cached.value;
      return acc;
    }, {});
  }

  public async clearByBucket(bucket: string): Promise<void> {
    if (process.env.DISABLE_DB_CACHE === 'true') {
      return;
    }
    await this.cachedValueDAO.removeByBucket(bucket);
  }

  protected saveToCache<T>(cached: ICachedValue): void {
    if (process.env.DISABLE_DB_CACHE === 'true') {
      return;
    }
    // Write to the cache in the background without waiting for the result. Gotta go fast!
    this.cachedValueDAO.upsert(cached).catch(err => {
      if (this.logger) {
        this.logger.silly(`cache write error`, err);
      }
    });
  }
}
